import '../broiler/poultryDesign.css';
import Button from '../shared/Button.js';
// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import {getProgramColor} from '../lib/programColors.js';
import {getReadableText} from '../lib/styles.js';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildEggDailyExportColumns} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import {resolveLayerGroupStatus} from '../lib/layerGroupStatus.js';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';

import InlineNotice from '../shared/InlineNotice.jsx';

import {DailyCardList, commentText} from '../shared/DailyRecordCards.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {useEggDailysProjectionRefresh, type EggDailysProjectionRefresh} from './eggDailysProjectionRefresh.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {EggDailysGroupsData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';

const EggDailyPage = React.lazy(() => import('./EggDailyPage.tsx'));
import {sb} from '../lib/supabase.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import Header from '../shared/Header.jsx';
import type {Notice} from '../shared/InlineNotice.js';
import type {SavedViewState} from '../lib/savedViewsApi.js';

const EGG_DAILYS_SURFACE_KEY = 'layer.eggs';
// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const EGG_DAILYS_GROUPS_PARAMS = Object.freeze({});

// NOT a prop from LayerEntry any more.
//
// `layerGroups` was the ppp-layer-groups-v1 app_store blob, threaded down from
// App's boot transaction as the last former-eager value this entry still
// carried to any route. The hub reads it itself now, through
// layer.eggDailys.groups, so `eggdailys` retires from the frozen app_store
// consumer list in tests/fixtures/former-eager-dependencies.json.
type EggDailysHubProps = {
  // The router owns one callback across the index and record route. In
  // particular, record deletion navigates back to the index before the read
  // resolves, so a record-local lifecycle fence would discard that refresh.
  refreshEggDailysProjection: EggDailysProjectionRefresh;
};

// One egg_dailys row. The select is `*` and the client carries no generated
// database types, so `data` arrives as `any`; this records the columns the view
// actually depends on — the reads below plus every column `rec` writes, since
// `{...r, ...rec}` merges one into the other.
//
// `date` is NOT optional: the filter row compares it relationally
// (`r.date >= fFrom`) with no guard, so the view already requires every report
// to carry its report date — the column migration 084 keys the active-daily
// unique index on. The count columns are `number | string` because a report
// reaches this table from the app (numeric) and from the webform (text).
type EggDailyRecord = {
  id?: string;
  date: string;
  team_member?: string | null;
  group1_name?: string | null;
  group1_count?: number | string | null;
  group2_name?: string | null;
  group2_count?: number | string | null;
  group3_name?: string | null;
  group3_count?: number | string | null;
  group4_name?: string | null;
  group4_count?: number | string | null;
  daily_dozen_count?: number | null;
  dozens_on_hand?: number | string | null;
  dozens_allocated_coastal_rancher?: number | string | null;
  comments?: string | null;
};

// One app_saved_views row, reduced to the columns this view reads. The names are
// the ones savedViewsApi's own select list already spells out; that module
// returns them as `unknown` because it never looks inside, so this is the first
// place that says what they are. `id` is the table's uuid primary key.
type SavedViewRow = {
  id: string;
  name?: string | null;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};

// What this surface actually stores in app_saved_views.view_state: its three
// filter fields, flat. See the note on the two write sites below — this is NOT
// the {filters, sortRules, viewMode} envelope savedViewsApi.SavedViewState
// declares.
type EggDailysViewState = {fTeam: string; fFrom: string; fTo: string};

// The `e.message` half of the `e.message || String(e)` fallbacks below. A caught
// value is `unknown` under strict mode, and `instanceof Error` is the wrong
// probe: supabase-js rejects with plain PostgrestError objects that carry
// `message` without being Errors. Returning '' for a value with no usable
// message leaves the existing `||` fallbacks behaving exactly as before.
function errMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const message = (e as {message?: unknown}).message;
    if (message) return String(message);
  }
  return '';
}

function EggDailysRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  // Keep this fence above the index/record split. A successful record delete
  // immediately navigates to the index, but the router remains mounted under
  // /layer/eggs/* long enough for its paired action-time publication to settle.
  const refreshEggDailysProjection = useEggDailysProjectionRefresh();
  const dailyId = location.pathname.startsWith('/layer/eggs/')
    ? location.pathname.slice('/layer/eggs/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Egg Daily Record…',
        ),
      },
      React.createElement(EggDailyPage, {
        sb,
        fmt,
        authState,
        refreshEggDailysProjection,
      }),
    );
  }
  return React.createElement(EggDailysHub, {refreshEggDailysProjection});
}

const EggDailysHub = ({refreshEggDailysProjection}: EggDailysHubProps) => {
  const {authState} = useAuth();
  // The list keeps its own record rows optimistic, but Home's 45-day timeline
  // and Layers Home's complete dashboard history are separate shared
  // projections. The router-owned action-time callback publishes both full-row
  // projections together, rather than routing Egg writes through App's global
  // fan-out.
  const {useState, useEffect} = React;
  // The ppp-layer-groups-v1 blob, read by this surface instead of arriving as a
  // LayerEntry prop off App's boot transaction. The assertion is the same one
  // this file already made — the blob's element shape lives in SQL, not in any
  // declaration this repo owns — moved to the one point where the resource
  // payload and this view's contract meet.
  const groupsResource = useRouteResource(RESOURCE.EGG_DAILYS_GROUPS, EGG_DAILYS_GROUPS_PARAMS);
  const groupsData = groupsResource.data as EggDailysGroupsData | null;
  // THIS SURFACE READS THE CANONICAL BLOB DIRECTLY, so its own three-state
  // verdict is the gate. PENDING while the payload is null, FAILED when the
  // hook errored or the reader could not vouch for the document, RESOLVED
  // otherwise - a missing document being resolved-and-empty.
  const canonicalStatus = resolveLayerGroupStatus({data: groupsData, error: groupsResource.error});
  // Rows the admin Add Report modal just created, projected over the server
  // pages so they show before the next read. (The edit override and delete hide
  // projections went with the unreachable edit modal, E12a.)
  const [createdRecords, setCreatedRecords] = useState<EggDailyRecord[]>([]);
  const [fTeam, setFTeam] = usePersistentViewState('layer.eggs.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('layer.eggs.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('layer.eggs.toFilter', '');
  const [exportNotice, setExportNotice] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  // `authState` is `AuthIdentity | false | null`, and `?.` only short-circuits
  // on null/undefined — `false?.user` is a property read on a boolean, not a
  // short circuit. The leading `&&` is the same truthiness guard PigBatchesView
  // already uses, and it produces the identical value for all three states:
  // null and false both fall through the trailing `|| null`.
  const myProfileId = (authState && authState.user?.id) || null;
  const savedViewsQuery = useSavedViewsData(EGG_DAILYS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  const navigate = useNavigate();

  // The list's rows, its cursor, its cancellation and its stale-key protection
  // are the paginated resource's now. `reload` is in the page key on purpose:
  // Retry must start a NEW complete sequence rather than same-key refetch an
  // already-partial list — the same contract layer.dailys.list documents.
  const dailysParams = React.useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const dailysResource = usePaginatedResource<EggDailyRecord, number>(RESOURCE.EGG_DAILYS_LIST, dailysParams);
  const {
    error: listError,
    hasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
  } = dailysResource;
  const loading = dailysResource.loading;
  const loadError = React.useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load egg reports. Please refresh the page. (' +
              (errMessage(listError) || String(listError)) +
              ')',
          }
        : listLoadMoreError
          ? {
              kind: 'error',
              message:
                'Could not load more egg reports. (' +
                (errMessage(listLoadMoreError) || String(listLoadMoreError)) +
                ')',
            }
          : null,
    [listError, listLoadMoreError],
  );

  // The server pages, with this session's local mutations projected over them.
  // Order is what the old single array produced: newly created rows first, then
  // the pages in server order, each row replaced by its override and skipped if
  // hidden. De-duplicated by id because the append loop below used to do that.
  const records = React.useMemo(() => {
    const seen = new Set<EggDailyRecord['id']>();
    const rows: EggDailyRecord[] = [];
    for (const row of [...createdRecords, ...dailysResource.rows]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    return rows;
  }, [createdRecords, dailysResource.rows]);

  // A Retry is a new server sequence, so its rows are authoritative again and
  // no transient local projection may leak into the new key.
  useEffect(() => {
    setCreatedRecords([]);
  }, [reloadKey]);

  // The eager "load every page" behaviour is unchanged; only the cursor moved.
  // `listLoadMoreError` is in the guard so a failed append stops the loop
  // instead of retrying it forever — the old `pgLoading` ref simply never reset
  // on failure, which had the same effect by accident.
  useEffect(() => {
    if (hasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [hasMore, listLoadingMore, listLoadMoreError, loadMore]);

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const [showAddModal, setShowAddModal] = useState(false);

  // `(t): t is string` annotates the SAME truthiness test `.filter(Boolean)` ran;
  // Boolean ignores filter's index/array arguments, so the surviving elements are
  // identical. The annotation is what lets each survivor be an <option> value.
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  const filtered = records.filter(
    (r) => matchesTeamMemberFilter(r.team_member, fTeam) && (!fFrom || r.date >= fFrom) && (!fTo || r.date <= fTo),
  );
  const totalEggs = filtered.reduce(
    // String() around counts that are already numeric on app-written rows:
    // parseInt is DECLARED to take a string and did the same ToString conversion
    // itself.
    (s, r) =>
      s +
      (parseInt(String(r.group1_count)) || 0) +
      (parseInt(String(r.group2_count)) || 0) +
      (parseInt(String(r.group3_count)) || 0) +
      (parseInt(String(r.group4_count)) || 0),
    0,
  );
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function eggDailysViewState(): EggDailysViewState {
    return {
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
    };
  }

  function applyEggDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    // `view_state` is opaque jsonb and savedViewsApi hands the row back without
    // describing it. Asserting the index shape rather than EggDailysViewState is
    // deliberate: it keeps each field `unknown`, so the three `typeof === 'string'`
    // tests below stay the real narrowing instead of decoration over a claim.
    const st = (view.view_state || {}) as Record<string, unknown>;
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyEggDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      // REAL FINDING, surfaced rather than fixed: savedViewsApi declares
      // `viewState: SavedViewState`, i.e. the {filters, sortRules, viewMode}
      // envelope buildViewState() produces. This surface has never written that
      // envelope — it stores its three filter fields flat and
      // applyEggDailysSavedView above reads them back flat, and
      // app_saved_views.view_state is opaque jsonb, so the two halves have
      // always agreed with each other and disagreed with the declared type.
      // Asserting through `unknown` records the mismatch at the one boundary it
      // happens; changing what is written here would orphan every stored
      // 'layer.eggs' view. Same assertion, same reason, in updateSelectedView.
      const created = (await createSavedView(sb, {
        surfaceKey: EGG_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: eggDailysViewState() as unknown as SavedViewState,
      })) as SavedViewRow | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      // Flat-vs-envelope assertion — see the note in submitSaveView above.
      await updateSavedView(sb, selectedView.id, {
        viewState: eggDailysViewState() as unknown as SavedViewState,
      });
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildEggDailyExportColumns();
    const ok = downloadCsv(csvFilename('egg-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildEggDailyExportColumns();
    const ok = printRows({
      title: 'Egg Dailys',
      subtitle: filtered.length + ' filtered egg reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  const fi = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'var(--surface)',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewGhostBtnS = {
    ...fi,
    color: 'var(--ink)',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const savedViewPrimaryBtnS = {
    ...savedViewGhostBtnS,
    border: `1px solid ${getProgramColor('layer')}`,
    color: getProgramColor('layer'),
  };
  const savedViewRadioLabelS = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-egg-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="poultry-page-title">Daily Reports</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
            <Button
              type="button"
              data-egg-dailys-export-csv="1"
              onClick={handleExportCsv}
              disabled={loading || !!loadError}
            >
              Export CSV
            </Button>
            <Button type="button" data-egg-dailys-print="1" onClick={handlePrintRows} disabled={loading || !!loadError}>
              Print
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowAddModal(true);
              }}
            >
              + Add Report
            </Button>
          </div>
        </div>
        {exportNotice && <div style={{marginBottom: 14, color: 'var(--danger-ink)', fontSize: 12}}>{exportNotice}</div>}
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="egg"
            onClose={() => setShowAddModal(false)}
            // The canonical read this hub already owns. The modal's own group
            // names come from the webform_config mirror, which lags a status
            // change by up to a maintenance run, so the verdict is what lets it
            // veto a group this page has already stopped listing - and refuse
            // to offer any group at all until that verdict exists.
            canonicalStatus={canonicalStatus}
            onRetryCanonicalStatus={groupsResource.refetch}
            // AdminAddReportModal is generic over the row type it hands back —
            // it inserts through a client with no generated database types, so
            // the shape is the caller's to name. This annotation is what binds
            // that type parameter: the modal calls `onSaved(data)` with the rows
            // its egg_dailys insert selected back.
            onSaved={(recs: EggDailyRecord[]) => {
              setCreatedRecords((p) => [...recs, ...p]);
              void refreshEggDailysProjection().catch(() => {});
            }}
          />
        )}
        {!loadError && (
          <>
            <div
              data-egg-dailys-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-egg-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-egg-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-egg-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-egg-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger-ink)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-egg-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-egg-dailys-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-egg-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveEggDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-egg-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveEggDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-egg-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button type="button" data-egg-dailys-saved-view-save onClick={submitSaveView} disabled={savedViewBusy}>
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center'}}>
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={{...fi, width: 130}} />
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>to</span>
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 130}} />
          <select
            data-egg-dailys-team-filter="1"
            value={teamMemberSelectValue(fTeam, teamOpts)}
            onChange={(e) => setFTeam(e.target.value)}
            style={fi}
          >
            <option value="">All team members</option>
            {teamOpts.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {(fTeam || fFrom || fTo) && (
            <Button
              onClick={() => {
                setFTeam('');
                setFFrom('');
                setFTo('');
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading...</div>}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No egg reports yet"
          filteredLabel="No egg reports match the current filters"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="egg"
            // DailyCardList is still JavaScript, and its `rows = []` default is all
            // TypeScript has to infer that prop from, so it reads as `never[]` — a
            // description of the DEFAULT VALUE, not of the component, which reads
            // d.date / d.id off every row. The assertion records that mismatch at
            // the one boundary where it bites; it comes out when DailyRecordCards
            // itself converts.
            rows={filtered as unknown as never[]}
            fmt={fmt}
            maxInitialRows={100}
            // DailyRecordCards is JavaScript, so these three callbacks carry
            // their row annotation here. Each is handed one element of `rows`,
            // which is `filtered` above.
            onOpen={(row) => {
              const d = row as EggDailyRecord;
              navigate('/layer/eggs/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, null)));
            }}
            rowAttrs={(row) => {
              const d = row as EggDailyRecord;
              return {'data-daily-row': d.id};
            }}
            mapRow={(row) => {
              const d = row as EggDailyRecord;
              // String() around counts that are already numeric on app-written
              // rows: parseInt/parseFloat are DECLARED to take a string and did
              // the same ToString conversion themselves.
              const total =
                (parseInt(String(d.group1_count)) || 0) +
                (parseInt(String(d.group2_count)) || 0) +
                (parseInt(String(d.group3_count)) || 0) +
                (parseInt(String(d.group4_count)) || 0);
              const breakdown = [
                [d.group1_name, d.group1_count],
                [d.group2_name, d.group2_count],
                [d.group3_name, d.group3_count],
                [d.group4_name, d.group4_count],
              ]
                .filter(([n, c]) => n && parseInt(String(c)) > 0)
                .map(([n, c]) => ({loc: n, n: c}));
              return {
                total,
                team: d.team_member || '—',
                breakdown,
                dozens: parseFloat(String(d.dozens_on_hand)) > 0 ? d.dozens_on_hand + ' doz' : null,
                comment: commentText(d.comments),
              };
            }}
          />
        )}
        {hasMore && (
          <div style={{textAlign: 'center', padding: '0.5rem', fontSize: 11, color: 'var(--ink-faint)'}}>
            Loading more records...
          </div>
        )}
      </div>
    </div>
  );
};

export default EggDailysRouter;
